import { useState } from "react";
import { useScrollReveal, getRevealClasses } from "@/hooks/useScrollReveal";
import { rankedBrokers } from "@/mocks/rankingsData";

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

interface BrokerCalcOption {
  name: string;
  logo: string;
  color: string;
  account: string;
  avgSpread: number;
  commissionPerSide: number;
  minDeposit: string;
}

const brokerOptions: BrokerCalcOption[] = [
  { name: "Tickmill", logo: "/images/TM.jpg", color: "#DC2626", account: "Raw (VIP)", avgSpread: 0.15, commissionPerSide: 3.0, minDeposit: "$100" },
  { name: "FxPro", logo: "/images/fxpro.jpg", color: "#F59E0B", account: "Raw+ (cTrader)", avgSpread: 0.18, commissionPerSide: 4.5, minDeposit: "$100" },
  { name: "IC Markets", logo: "/images/ic.jpg", color: "#EF4444", account: "cTrader Raw", avgSpread: 0.12, commissionPerSide: 3.0, minDeposit: "$200" },
  { name: "XM", logo: "/images/xm.jpg", color: "#8B5CF6", account: "Ultra Low", avgSpread: 0.65, commissionPerSide: 0, minDeposit: "$5" },
  { name: "Axi", logo: "/images/axi.jpg", color: "#3B82F6", account: "Pro", avgSpread: 0.17, commissionPerSide: 3.5, minDeposit: "$500" },
  { name: "FXTM", logo: "/images/fx.jpg", color: "#EA580C", account: "Advantage", avgSpread: 0.15, commissionPerSide: 3.5, minDeposit: "$200" },
];

const radarDimLabels = [
  "Trading Cost",
  "Execution Speed",
  "Platform & Tools",
  "Regulatory Trust",
  "Customer Support",
  "Deposit & Withdrawal",
];

/* ------------------------------------------------------------------ */
/*  Radar helpers                                                      */
/* ------------------------------------------------------------------ */

function computeRadar6(scores: number[], cx: number, cy: number, r: number) {
  const n = 6;
  return scores.map((s, i) => {
    const angle = (2 * Math.PI * i) / n - Math.PI / 2;
    const dist = (s / 10) * r;
    return { x: cx + dist * Math.cos(angle), y: cy + dist * Math.sin(angle) };
  });
}

function buildRadarGrid6(cx: number, cy: number, r: number, levels: number) {
  const n = 6;
  const rings: { x: number; y: number }[][] = [];
  for (let l = 1; l <= levels; l++) {
    const ring: { x: number; y: number }[] = [];
    const dist = (l / levels) * r;
    for (let i = 0; i < n; i++) {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
      ring.push({ x: cx + dist * Math.cos(angle), y: cy + dist * Math.sin(angle) });
    }
    rings.push(ring);
  }
  return rings;
}

function getBenchmarkBroker(selected: string): string {
  return selected === "Tickmill" ? "XM" : "Tickmill";
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function InteractiveAnalytics() {
  const { ref, isVisible } = useScrollReveal(0.1);

  /* ── Calculator state ───────────────────────────────────────── */
  const [selectedBroker, setSelectedBroker] = useState("XM");
  const [lots, setLots] = useState(1);

  /* ── Right panel tab ──────────────────────────────────────────── */
  const [analyticsTab, setAnalyticsTab] = useState<"radar" | "bar">("radar");

  /* ── Calculator math ──────────────────────────────────────────── */
  const selectedOpt = brokerOptions.find((b) => b.name === selectedBroker)!;
  const pipValue = 10;
  const spreadCost = selectedOpt.avgSpread * pipValue * lots;
  const commissionCost = selectedOpt.commissionPerSide * 2 * lots;
  const totalCost = spreadCost + commissionCost;

  /* ── Radar data ───────────────────────────────────────────────── */
  const baselineBroker = rankedBrokers.find((b) => b.name === selectedBroker)!;
  const benchmarkName = getBenchmarkBroker(selectedBroker);
  const benchmarkBroker = rankedBrokers.find((b) => b.name === benchmarkName)!;

  const baselineScores = [
    { dim: "Trading Cost", val: baselineBroker.dimensions.cost },
    { dim: "Execution Speed", val: baselineBroker.dimensions.execution },
    { dim: "Platform & Tools", val: baselineBroker.dimensions.platforms },
    { dim: "Regulatory Trust", val: baselineBroker.dimensions.regulation },
    { dim: "Customer Support", val: baselineBroker.dimensions.support },
    { dim: "Deposit & Withdrawal", val: baselineBroker.dimensions.depositWithdrawal },
  ];
  const benchmarkScores = [
    { dim: "Trading Cost", val: benchmarkBroker.dimensions.cost },
    { dim: "Execution Speed", val: benchmarkBroker.dimensions.execution },
    { dim: "Platform & Tools", val: benchmarkBroker.dimensions.platforms },
    { dim: "Regulatory Trust", val: benchmarkBroker.dimensions.regulation },
    { dim: "Customer Support", val: benchmarkBroker.dimensions.support },
    { dim: "Deposit & Withdrawal", val: benchmarkBroker.dimensions.depositWithdrawal },
  ];
  const baselineRawScores = baselineScores.map((s) => s.val);
  const benchmarkRawScores = benchmarkScores.map((s) => s.val);

  /* ── Radar hover tooltip state ───────────────────────────────── */
  const [radarTooltip, setRadarTooltip] = useState<{
    broker: string;
    scores: { dim: string; val: number }[];
    color: string;
    x: number;
    y: number;
  } | null>(null);

  /* ── Radar SVG constants ──────────────────────────────────────── */
  const svgW = 400;
  const svgH = 360;
  const cx = 200;
  const cy = 180;
  const r = 135;
  const rings = buildRadarGrid6(cx, cy, r, 5);

  /* ── Bar chart data ─────────────────────────────────────────── */
  const barData = rankedBrokers
    .map((b) => ({ name: b.name, logo: b.logo, color: b.color, score: b.dimensions.execution }))
    .sort((a, b) => b.score - a.score);

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#111827] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Section header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#8B5CF6]/30 bg-[#8B5CF6]/8 mb-5">
            <i className="ri-dashboard-line" style={{ color: "#8B5CF6" }}></i>
            <span className="text-sm text-[#F8FAFC] font-medium">Interactive Analytics</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">
            Actionable Insights — Don't Just Read, Interact
          </h2>
          <p className="text-base text-[#94A3B8] max-w-2xl mx-auto">
            Compare real costs with live numbers. Visualize broker strengths instantly.
            No marketing fluff — just data you can act on.
          </p>
        </div>

        {/* ── Two-column layout ──────────────────────────────────── */}
        <div className="flex flex-col lg:flex-row gap-6">

          {/* LEFT: Live Cost Calculator */}
          <div className="lg:w-[380px] xl:w-[400px] shrink-0">
            <div className="bg-[#1A2235] border border-[#2A3345] rounded-2xl p-6 h-full flex flex-col">
              <h3 className="text-lg font-bold text-[#F8FAFC]">Live Cost Calculator</h3>
              <p className="text-xs text-[#94A3B8] mt-1 leading-relaxed">
                Calculate Total Cost (Spread + Commission) per 1 Standard Lot (EURUSD).
              </p>

              {/* Select Broker */}
              <div className="mt-6">
                <label className="text-xs text-[#94A3B8] font-medium mb-1.5 block">Select Broker</label>
                <div className="relative">
                  <select
                    value={selectedBroker}
                    onChange={(e) => setSelectedBroker(e.target.value)}
                    className="w-full appearance-none bg-[#111827] border border-[#2A3345] hover:border-[#374151] rounded-lg px-4 py-2.5 pr-10 text-sm text-[#F8FAFC] outline-none focus:border-[#3B82F6] transition-colors cursor-pointer"
                  >
                    {brokerOptions.map((b) => (
                      <option key={b.name} value={b.name}>
                        {b.name} ({b.account}) — {b.commissionPerSide > 0 ? `$${b.commissionPerSide.toFixed(1)}/side` : "Zero Comm"}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] pointer-events-none text-lg"></i>
                </div>
              </div>

              {/* Trade Size */}
              <div className="mt-5">
                <label className="text-xs text-[#94A3B8] font-medium mb-1.5 block">Trade Size (Lots)</label>
                <input
                  type="number"
                  min={0.01}
                  step={0.1}
                  value={lots}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v) && v >= 0.01) setLots(v);
                  }}
                  className="w-full bg-[#111827] border border-[#2A3345] hover:border-[#374151] rounded-lg px-4 py-2.5 text-sm text-[#F8FAFC] font-mono outline-none focus:border-[#3B82F6] transition-colors"
                />
              </div>

              {/* Quick lot buttons */}
              <div className="flex flex-wrap gap-2 mt-3">
                {[0.1, 0.5, 1, 2, 5, 10].map((v) => (
                  <button
                    key={v}
                    onClick={() => setLots(v)}
                    className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-all whitespace-nowrap ${
                      lots === v
                        ? "bg-[#3B82F6] text-white"
                        : "bg-[#1F2937] text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#374151]"
                    }`}
                  >
                    {v} lot{v > 1 ? "s" : ""}
                  </button>
                ))}
              </div>

              {/* Results Card */}
              <div className="mt-6 bg-[#111827] rounded-xl p-5 border border-[#1F2937]">
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-xs text-[#94A3B8]">
                    Spread Cost ({selectedOpt.avgSpread.toFixed(1)} pips):
                  </span>
                  <span className="text-sm font-mono font-bold text-[#F8FAFC]">
                    ${spreadCost.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs text-[#94A3B8]">Commission Cost:</span>
                  <span
                    className={`text-sm font-mono font-bold ${
                      commissionCost > 0 ? "text-[#F59E0B]" : "text-[#10B981]"
                    }`}
                  >
                    ${commissionCost.toFixed(2)}
                  </span>
                </div>
                <div className="border-t border-[#2A3345] pt-3 flex items-center justify-between">
                  <span className="text-sm font-bold text-[#F8FAFC]">Total Cost:</span>
                  <span className="text-xl font-black text-[#3B82F6] font-mono">
                    ${totalCost.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Footnote */}
              <div className="mt-4 flex items-start gap-2">
                <i className="ri-information-line text-[#64748B] text-xs mt-0.5"></i>
                <p className="text-[10px] text-[#64748B] leading-relaxed">
                  Based on {selectedOpt.account} account. Real spread may vary by market conditions.
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT: Performance Analytics */}
          <div className="flex-1 min-w-0">
            <div className="bg-[#1A2235] border border-[#2A3345] rounded-2xl p-6 h-full flex flex-col">
              {/* Header + Tabs */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <h3 className="text-lg font-bold text-[#F8FAFC]">Performance Analytics</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setAnalyticsTab("radar")}
                    className={`px-3.5 py-1.5 rounded-md text-[11px] font-semibold transition-all whitespace-nowrap ${
                      analyticsTab === "radar"
                        ? "bg-[#3B82F6] text-white"
                        : "bg-[#1F2937] text-[#94A3B8] hover:text-[#F8FAFC]"
                    }`}
                  >
                    Capability Radar
                  </button>
                  <button
                    onClick={() => setAnalyticsTab("bar")}
                    className={`px-3.5 py-1.5 rounded-md text-[11px] font-semibold transition-all whitespace-nowrap ${
                      analyticsTab === "bar"
                        ? "bg-[#3B82F6] text-white"
                        : "bg-[#1F2937] text-[#94A3B8] hover:text-[#F8FAFC]"
                    }`}
                  >
                    Execution Speed
                  </button>
                </div>
              </div>

              {/* ── Radar Chart ───────────────────────────────────── */}
              {analyticsTab === "radar" && (
                <div className="flex-1 flex flex-col items-center justify-center">
                  <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full max-w-[420px] h-auto">
                    {/* Grid rings */}
                    {rings.map((ring, ri) => (
                      <polygon
                        key={`ring-${ri}`}
                        points={ring.map((p) => `${p.x},${p.y}`).join(" ")}
                        fill="none"
                        stroke="#2A3345"
                        strokeWidth={ri === rings.length - 1 ? 1.5 : 0.8}
                        strokeDasharray={ri < rings.length - 1 ? "3 3" : "0"}
                      />
                    ))}

                    {/* Axes */}
                    {radarDimLabels.map((_, i) => {
                      const angle = (2 * Math.PI * i) / 6 - Math.PI / 2;
                      const ex = cx + r * Math.cos(angle);
                      const ey = cy + r * Math.sin(angle);
                      return <line key={`axis-${i}`} x1={cx} y1={cy} x2={ex} y2={ey} stroke="#2A3345" strokeWidth={0.8} />;
                    })}

                    {/* Labels */}
                    {radarDimLabels.map((label, i) => {
                      const angle = (2 * Math.PI * i) / 6 - Math.PI / 2;
                      const lr = r + 24;
                      const lx = cx + lr * Math.cos(angle);
                      const ly = cy + lr * Math.sin(angle);
                      const anchor = i === 0 ? "middle" : i < 3 ? "start" : i === 3 ? "middle" : "end";
                      return (
                        <text
                          key={`label-${i}`}
                          x={lx}
                          y={ly}
                          textAnchor={anchor}
                          dominantBaseline="central"
                          className="fill-[#94A3B8]"
                          style={{ fontSize: "10px", fontFamily: "inherit", fontWeight: 500 }}
                        >
                          {label}
                        </text>
                      );
                    })}

                    {/* Benchmark polygon — gray outline only */}
                    {(() => {
                      const pts = computeRadar6(benchmarkRawScores, cx, cy, r);
                      const ptsStr = pts.map((p) => `${p.x},${p.y}`).join(" ");
                      return (
                        <g
                          key="benchmark-poly"
                          style={{ cursor: "pointer" }}
                          onMouseEnter={(e) => {
                            const svgEl = (e.currentTarget.closest("svg") as SVGSVGElement)!;
                            const rect = svgEl.getBoundingClientRect();
                            const svgX = ((e.clientX - rect.left) / rect.width) * svgW;
                            const svgY = ((e.clientY - rect.top) / rect.height) * svgH;
                            setRadarTooltip({
                              broker: benchmarkBroker.name,
                              scores: benchmarkScores,
                              color: "#94A3B8",
                              x: svgX,
                              y: svgY,
                            });
                          }}
                          onMouseMove={(e) => {
                            const svgEl = (e.currentTarget.closest("svg") as SVGSVGElement)!;
                            const rect = svgEl.getBoundingClientRect();
                            const svgX = ((e.clientX - rect.left) / rect.width) * svgW;
                            const svgY = ((e.clientY - rect.top) / rect.height) * svgH;
                            setRadarTooltip((prev) => (prev ? { ...prev, x: svgX, y: svgY } : prev));
                          }}
                          onMouseLeave={() => setRadarTooltip(null)}
                        >
                          {/* Invisible larger hit area */}
                          <polygon
                            points={ptsStr}
                            fill="transparent"
                            stroke="transparent"
                            strokeWidth={12}
                          />
                          <polygon
                            points={ptsStr}
                            fill="none"
                            stroke="#94A3B8"
                            strokeOpacity={radarTooltip?.broker === benchmarkBroker.name ? 0.9 : 0.5}
                            strokeWidth={radarTooltip?.broker === benchmarkBroker.name ? 2.5 : 1.5}
                            style={{ transition: "stroke-opacity 0.2s, stroke-width 0.2s" }}
                          />
                          {pts.map((p, i) => (
                            <circle
                              key={`bm-dot-${i}`}
                              cx={p.x}
                              cy={p.y}
                              r={radarTooltip?.broker === benchmarkBroker.name ? 5 : 3}
                              fill="#94A3B8"
                              fillOpacity={radarTooltip?.broker === benchmarkBroker.name ? 0.9 : 0.6}
                              style={{ transition: "fill-opacity 0.2s, r 0.2s" }}
                            />
                          ))}
                        </g>
                      );
                    })()}

                    {/* Baseline polygon — selected broker, filled */}
                    {(() => {
                      const pts = computeRadar6(baselineRawScores, cx, cy, r);
                      const ptsStr = pts.map((p) => `${p.x},${p.y}`).join(" ");
                      return (
                        <g
                          key="baseline-poly"
                          style={{ cursor: "pointer" }}
                          onMouseEnter={(e) => {
                            const svgEl = (e.currentTarget.closest("svg") as SVGSVGElement)!;
                            const rect = svgEl.getBoundingClientRect();
                            const svgX = ((e.clientX - rect.left) / rect.width) * svgW;
                            const svgY = ((e.clientY - rect.top) / rect.height) * svgH;
                            setRadarTooltip({
                              broker: baselineBroker.name,
                              scores: baselineScores,
                              color: baselineBroker.color,
                              x: svgX,
                              y: svgY,
                            });
                          }}
                          onMouseMove={(e) => {
                            const svgEl = (e.currentTarget.closest("svg") as SVGSVGElement)!;
                            const rect = svgEl.getBoundingClientRect();
                            const svgX = ((e.clientX - rect.left) / rect.width) * svgW;
                            const svgY = ((e.clientY - rect.top) / rect.height) * svgH;
                            setRadarTooltip((prev) => (prev ? { ...prev, x: svgX, y: svgY } : prev));
                          }}
                          onMouseLeave={() => setRadarTooltip(null)}
                        >
                          {/* Invisible larger hit area */}
                          <polygon
                            points={ptsStr}
                            fill="transparent"
                            stroke="transparent"
                            strokeWidth={12}
                          />
                          <polygon
                            points={ptsStr}
                            fill={baselineBroker.color}
                            fillOpacity={radarTooltip?.broker === baselineBroker.name ? 0.25 : 0.15}
                            stroke={baselineBroker.color}
                            strokeOpacity={radarTooltip?.broker === baselineBroker.name ? 1 : 0.8}
                            strokeWidth={radarTooltip?.broker === baselineBroker.name ? 3 : 2}
                            style={{ transition: "fill-opacity 0.2s, stroke-opacity 0.2s, stroke-width 0.2s" }}
                          />
                          {pts.map((p, i) => (
                            <circle
                              key={`bl-dot-${i}`}
                              cx={p.x}
                              cy={p.y}
                              r={radarTooltip?.broker === baselineBroker.name ? 5.5 : 4}
                              fill={baselineBroker.color}
                              stroke="#1A2235"
                              strokeWidth={1.5}
                              style={{ transition: "r 0.2s" }}
                            />
                          ))}
                        </g>
                      );
                    })()}

                    {/* ── Hover Tooltip ────────────────────────── */}
                    {radarTooltip && (
                      <g pointerEvents="none">
                        {/* Tooltip background */}
                        <rect
                          x={Math.min(radarTooltip.x + 12, svgW - 200)}
                          y={Math.min(radarTooltip.y + 12, svgH - 190)}
                          width={188}
                          height={190}
                          rx={10}
                          fill="#0F172A"
                          stroke={radarTooltip.color}
                          strokeOpacity={0.5}
                          strokeWidth={1.5}
                          filter="url(#tooltipShadow)"
                        />
                        {/* Shadow filter */}
                        <defs>
                          <filter id="tooltipShadow" x="-20%" y="-20%" width="140%" height="140%">
                            <feDropShadow dx={0} dy={2} stdDeviation={4} floodColor="#000" floodOpacity={0.4} />
                          </filter>
                        </defs>
                        {/* Tooltip header */}
                        <text
                          x={Math.min(radarTooltip.x + 12, svgW - 200) + 14}
                          y={Math.min(radarTooltip.y + 12, svgH - 190) + 20}
                          fill={radarTooltip.color}
                          style={{ fontSize: "13px", fontFamily: "inherit", fontWeight: 700 }}
                        >
                          {radarTooltip.broker}
                        </text>
                        {/* Divider line */}
                        <line
                          x1={Math.min(radarTooltip.x + 12, svgW - 200) + 14}
                          y1={Math.min(radarTooltip.y + 12, svgH - 190) + 28}
                          x2={Math.min(radarTooltip.x + 12, svgW - 200) + 174}
                          y2={Math.min(radarTooltip.y + 12, svgH - 190) + 28}
                          stroke={radarTooltip.color}
                          strokeOpacity={0.2}
                          strokeWidth={0.8}
                        />
                        {/* Dimension scores */}
                        {radarTooltip.scores.map((s, i) => {
                          const ty = Math.min(radarTooltip.y + 12, svgH - 190) + 46 + i * 22;
                          return (
                            <g key={`tt-${i}`}>
                              <text
                                x={Math.min(radarTooltip.x + 12, svgW - 200) + 14}
                                y={ty}
                                fill="#94A3B8"
                                style={{ fontSize: "10px", fontFamily: "inherit", fontWeight: 500 }}
                              >
                                {s.dim}
                              </text>
                              <text
                                x={Math.min(radarTooltip.x + 12, svgW - 200) + 170}
                                y={ty}
                                fill="#F8FAFC"
                                textAnchor="end"
                                style={{ fontSize: "11px", fontFamily: "monospace", fontWeight: 700 }}
                              >
                                {s.val.toFixed(1)}
                              </text>
                              {/* Mini bar */}
                              <rect
                                x={Math.min(radarTooltip.x + 12, svgW - 200) + 14}
                                y={ty + 3}
                                width={Math.min((s.val / 10) * 152, 152)}
                                height={2.5}
                                rx={1.25}
                                fill={radarTooltip.color}
                                fillOpacity={0.3}
                              />
                            </g>
                          );
                        })}
                      </g>
                    )}
                  </svg>

                  {/* Legend */}
                  <div className="flex items-center justify-center gap-6 mt-4">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-5 h-3 rounded-sm"
                        style={{ backgroundColor: baselineBroker.color, opacity: 0.7 }}
                      />
                      <span className="text-xs font-medium text-[#F8FAFC]">
                        {baselineBroker.name} (Selected)
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-5 h-3 rounded-sm border border-[#94A3B8]" />
                      <span className="text-xs font-medium text-[#94A3B8]">
                        {benchmarkBroker.name} (Benchmark)
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Bar Chart — Execution Speed ─────────────────── */}
              {analyticsTab === "bar" && (
                <div className="flex-1">
                  <div className="space-y-3">
                    {barData.map((broker, idx) => (
                      <div key={broker.name}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
                              style={{
                                backgroundColor: `${broker.color}15`,
                                borderColor: `${broker.color}30`,
                                borderWidth: 1,
                                borderStyle: "solid",
                              }}
                            >
                              <img src={broker.logo} alt={broker.name} className="w-6 h-6 rounded object-cover" />
                            </div>
                            <span className="text-xs font-bold text-[#F8FAFC]">{broker.name}</span>
                          </div>
                          <span className="text-sm font-black text-[#F8FAFC] font-mono">
                            {broker.score.toFixed(1)}
                            <span className="text-[10px] text-[#64748B] font-normal">/10</span>
                          </span>
                        </div>
                        <div className="h-6 bg-[#1F2937] rounded-md overflow-hidden relative">
                          <div
                            className="h-full rounded-md transition-all duration-1000 ease-out flex items-center justify-end pr-2"
                            style={{
                              width: `${broker.score * 10}%`,
                              background:
                                idx === 0
                                  ? `linear-gradient(90deg, ${broker.color}99, ${broker.color})`
                                  : `linear-gradient(90deg, ${broker.color}40, ${broker.color}99)`,
                            }}
                          >
                            {idx === 0 && (
                              <span className="text-[9px] font-bold text-white drop-shadow-sm whitespace-nowrap">
                                <i className="ri-trophy-line mr-1"></i>Fastest
                              </span>
                            )}
                          </div>
                          <div className="absolute top-1/2 -translate-y-1/2 left-2.5">
                            <span
                              className={`text-[9px] font-black ${
                                idx === 0
                                  ? "text-[#F59E0B]"
                                  : idx === 1
                                    ? "text-[#94A3B8]"
                                    : idx === 2
                                      ? "text-[#D97706]"
                                      : "text-[#64748B]"
                              }`}
                            >
                              #{idx + 1}
                            </span>
                          </div>
                        </div>
                        {idx === 0 && (
                          <div className="flex items-center gap-1 mt-1 pl-8">
                            <i className="ri-flashlight-line text-[10px] text-[#10B981]"></i>
                            <span className="text-[10px] text-[#10B981]">
                              ~71ms avg · 99.90% fill rate
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex items-start gap-2 bg-[#F59E0B]/5 border border-[#F59E0B]/15 rounded-lg p-3">
                    <i className="ri-information-line text-[#F59E0B] text-xs mt-0.5"></i>
                    <p className="text-[10px] text-[#94A3B8] leading-relaxed">
                      Differences under 50ms are imperceptible for manual traders. Fill rate matters more than raw speed.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}